import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Chip,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import RestoreIcon from '@mui/icons-material/RestoreOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import { colors } from '../../theme';

const API_URL = import.meta.env.VITE_API_URL || '';

const LOG_LEVELS = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'] as const;

const DURATION_PRESETS = [
    { label: '5 minutes', value: 'PT5M' },
    { label: '15 minutes', value: 'PT15M' },
    { label: '30 minutes', value: 'PT30M' },
    { label: '1 hour', value: 'PT1H' },
    { label: '2 hours', value: 'PT2H' },
    { label: '4 hours', value: 'PT4H' },
    { label: '8 hours', value: 'PT8H' },
    { label: '24 hours', value: 'PT24H' },
] as const;

interface LogLevelCategory {
    level: string;
    setBy: string | null;
    setAt: string | null;
    expiresAt: string | null;
}

interface LoggingConfig {
    nodeService: LogLevelCategory;
    controllers: LogLevelCategory;
    autoResetEnabled: boolean;
    autoResetDuration: string;
}

function formatRelativeTime(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const absDiffMs = Math.abs(diffMs);

    if (absDiffMs < 60_000) return diffMs > 0 ? 'in less than a minute' : 'less than a minute ago';

    const minutes = Math.floor(absDiffMs / 60_000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    let label: string;
    if (days > 0) label = `${days}d ${hours % 24}h`;
    else if (hours > 0) label = `${hours}h ${minutes % 60}m`;
    else label = `${minutes}m`;

    return diffMs > 0 ? `in ${label}` : `${label} ago`;
}

function formatTimestamp(dateStr: string | null): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
}

export const DiagnosticsTab = ({ nodeId }: { nodeId: string }) => {
    const [config, setConfig] = useState<LoggingConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);

    // Form state
    const [nodeServiceLevel, setNodeServiceLevel] = useState('Information');
    const [controllersLevel, setControllersLevel] = useState('Information');
    const [autoResetEnabled, setAutoResetEnabled] = useState(true);
    const [autoResetDuration, setAutoResetDuration] = useState('PT1H');

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/api/admin/devices/${nodeId}/diagnostics/logging`, {
                credentials: 'include',
            });
            if (!res.ok) {
                const body = await res.text();
                throw new Error(body || `HTTP ${res.status}`);
            }
            const data: LoggingConfig = await res.json();
            setConfig(data);
            setNodeServiceLevel(data.nodeService.level);
            setControllersLevel(data.controllers.level);
            setAutoResetEnabled(data.autoResetEnabled);
            setAutoResetDuration(data.autoResetDuration);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load logging configuration');
        } finally {
            setLoading(false);
        }
    }, [nodeId]);

    useEffect(() => {
        fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeId]);

    const handleApply = async () => {
        setSaving(true);
        setError(null);
        try {
            const body = {
                nodeService: nodeServiceLevel === 'Information' ? null : nodeServiceLevel,
                controllers: controllersLevel === 'Information' ? null : controllersLevel,
                autoResetEnabled,
                autoResetDuration,
            };
            const res = await fetch(`${API_URL}/api/admin/devices/${nodeId}/diagnostics/logging`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }
            await fetchConfig();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to apply configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        setResetDialogOpen(false);
        setSaving(true);
        setError(null);
        try {
            const body = { nodeService: null, controllers: null };
            const res = await fetch(`${API_URL}/api/admin/devices/${nodeId}/diagnostics/logging`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }
            await fetchConfig();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to reset configuration');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
            </Box>
        );
    }

    if (error && !config) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!config) return null;

    const isOverrideActive =
        config.nodeService.level !== 'Information' || config.controllers.level !== 'Information';

    const isExpired =
        isOverrideActive &&
        config.nodeService.expiresAt !== null &&
        new Date(config.nodeService.expiresAt) < new Date();

    const hasChanges =
        nodeServiceLevel !== config.nodeService.level ||
        controllersLevel !== config.controllers.level ||
        autoResetEnabled !== config.autoResetEnabled ||
        autoResetDuration !== config.autoResetDuration;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <TuneIcon sx={{ fontSize: 28, color: colors.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Logging Configuration
                </Typography>
                {isOverrideActive && !isExpired && (
                    <Chip
                        label="Override Active"
                        size="small"
                        sx={{
                            bgcolor: alpha(colors.warning, 0.1),
                            color: colors.warning,
                            border: `1px solid ${alpha(colors.warning, 0.2)}`,
                            fontWeight: 600,
                        }}
                    />
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Expired override alert */}
            {isExpired && (
                <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{
                        mb: 2,
                        opacity: 0.7,
                        bgcolor: alpha(colors.warning, 0.05),
                        border: `1px solid ${alpha(colors.warning, 0.15)}`,
                    }}
                >
                    Override has expired. Logging levels have reverted to defaults.
                    The configuration will be cleaned up on the next node check-in.
                </Alert>
            )}

            {/* Status display */}
            {isOverrideActive && config.nodeService.setBy && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(colors.primary, 0.04),
                        border: `1px solid ${alpha(colors.primary, 0.1)}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 16, color: colors.slate[500] }} />
                        <Typography variant="body2" sx={{ color: colors.slate[600] }}>
                            Set by <strong>{config.nodeService.setBy}</strong>
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: colors.slate[500] }} />
                        <Typography variant="body2" sx={{ color: colors.slate[600] }}>
                            Changed {formatRelativeTime(config.nodeService.setAt)} ({formatTimestamp(config.nodeService.setAt)})
                        </Typography>
                    </Box>
                    {config.nodeService.expiresAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon sx={{ fontSize: 16, color: isExpired ? colors.error : colors.slate[500] }} />
                            <Typography
                                variant="body2"
                                sx={{ color: isExpired ? colors.error : colors.slate[600] }}
                            >
                                {isExpired ? 'Expired' : 'Expires'} {formatRelativeTime(config.nodeService.expiresAt)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Log level selectors */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel>Node Service Log Level</InputLabel>
                    <Select
                        value={nodeServiceLevel}
                        label="Node Service Log Level"
                        onChange={(e) => setNodeServiceLevel(e.target.value)}
                    >
                        {LOG_LEVELS.map((level) => (
                            <MenuItem key={level} value={level}>
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel>Controllers Log Level</InputLabel>
                    <Select
                        value={controllersLevel}
                        label="Controllers Log Level"
                        onChange={(e) => setControllersLevel(e.target.value)}
                    >
                        {LOG_LEVELS.map((level) => (
                            <MenuItem key={level} value={level}>
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Auto-reset controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={autoResetEnabled}
                            onChange={(e) => setAutoResetEnabled(e.target.checked)}
                            color="primary"
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Auto-reset to defaults
                        </Typography>
                    }
                />

                {autoResetEnabled && (
                    <FormControl sx={{ minWidth: 160 }} size="small">
                        <InputLabel>Reset After</InputLabel>
                        <Select
                            value={autoResetDuration}
                            label="Reset After"
                            onChange={(e) => setAutoResetDuration(e.target.value)}
                        >
                            {DURATION_PRESETS.map((preset) => (
                                <MenuItem key={preset.value} value={preset.value}>
                                    {preset.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    disabled={saving || !hasChanges}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                    sx={{ minWidth: 100 }}
                >
                    {saving ? 'Applying...' : 'Apply'}
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setResetDialogOpen(true)}
                    disabled={saving || !isOverrideActive}
                    startIcon={<RestoreIcon />}
                >
                    Reset to Default
                </Button>
            </Box>

            {/* Reset confirmation dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Reset Logging Configuration?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will reset both Node Service and Controllers log levels back to
                        <strong> Information</strong> (the default). Any active override will be removed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleReset} color="warning" variant="contained">
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
